/**
 * Release 3 D — board "Share" dialog: who can see the board (S2 visibility) and the people invited to it (DEL-2 board
 * guests). A guest is a workspace member with the guest role (mask 0) whose access comes from an override on this
 * board: view, comment (view + comment) or upload (view + comment + upload). Inviting mails a sign-in link that
 * lands on the board; removing the last board a guest had suspends them.
 */
import { useState, type FormEvent } from 'react';
import { Mail, Trash2, UserPlus } from 'lucide-react';
import type { Board, Schemas } from '@/lib/api/types';
import { useBoardGuests, useInviteBoardGuest, useRemoveBoardGuest, useUpdateBoardGuest } from '@/lib/api/queries';
import { toast } from '@/stores/ui';
import { isApiError } from '@/lib/api/errors';
import { Dialog } from '@/components/Dialog';
import { Button, IconButton } from '@/components/Button';
import { Input, Label, Select, Textarea } from '@/components/Input';
import { Avatar, Badge, Spinner } from '@/components/Misc';
import { BoardVisibilityControl } from './BoardVisibilityControl';
import { isPrivateBoard } from './visibility';

type GuestAccess = Schemas['GuestAccess'];
const ACCESS: { value: GuestAccess; label: string }[] = [
  { value: 'view', label: 'Can view' },
  { value: 'comment', label: 'Can comment' },
  { value: 'upload', label: 'Can upload' },
];

export function BoardShareDialog({ board, onClose }: { board: Board; onClose: () => void }) {
  return (
    <Dialog open onClose={onClose} title={`Share “${board.name}”`} footer={<Button onClick={onClose}>Done</Button>} width="max-w-xl">
      <div className="space-y-5">
        <BoardVisibilityControl board={board} />
        <BoardGuestsPanel board={board} />
      </div>
    </Dialog>
  );
}

export function BoardGuestsPanel({ board }: { board: Board }) {
  const guests = useBoardGuests(board.id);
  const invite = useInviteBoardGuest(board.id);
  const update = useUpdateBoardGuest(board.id);
  const remove = useRemoveBoardGuest(board.id);
  const [email, setEmail] = useState('');
  const [access, setAccess] = useState<GuestAccess>('view');
  const [message, setMessage] = useState('');
  const [showMessage, setShowMessage] = useState(false);
  const onErr = (e: unknown) => toast({ kind: 'error', title: isApiError(e) ? e.message : 'Could not update guests' });

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const to = email.trim();
    if (!to) return;
    invite.mutate(
      { email: to, access, ...(message.trim() ? { message: message.trim() } : {}) },
      {
        onSuccess: (g) => {
          setEmail('');
          setMessage('');
          toast({ kind: 'success', title: `Invited ${g.member.user.email}` });
        },
        onError: onErr,
      },
    );
  };

  return (
    <section aria-label="Guests" data-board-guests>
      <Label htmlFor="guest-email">Invite people</Label>
      <p className="mb-1.5 text-xs text-fg-subtle">
        {isPrivateBoard(board) ? 'This board is private: only its owner, workspace admins and the people below can see it.' : 'Guests see this board (and its sub-boards) only — nothing else in the workspace.'}
      </p>
      <form onSubmit={submit} className="flex flex-wrap items-end gap-2" data-guest-invite>
        <div className="min-w-[14rem] flex-1">
          <Input id="guest-email" type="email" placeholder="name@example.com" value={email} onChange={(e) => setEmail(e.target.value)} required autoComplete="off" />
        </div>
        <div>
          <Label htmlFor="guest-access" className="sr-only">Access</Label>
          <Select id="guest-access" value={access} onChange={(e) => setAccess(e.target.value as GuestAccess)} className="h-9">
            {ACCESS.map((a) => (
              <option key={a.value} value={a.value}>
                {a.label}
              </option>
            ))}
          </Select>
        </div>
        <Button type="submit" variant="primary" disabled={!email.trim() || invite.isPending}>
          <UserPlus size={14} /> Invite
        </Button>
        <button type="button" className="text-xs text-fg-subtle underline-offset-2 hover:underline" onClick={() => setShowMessage((v) => !v)} aria-expanded={showMessage}>
          {showMessage ? 'Hide message' : 'Add a message'}
        </button>
        {showMessage && (
          <div className="w-full">
            <Label htmlFor="guest-message" className="sr-only">Message</Label>
            <Textarea id="guest-message" rows={2} maxLength={1000} placeholder="Optional note for the invitation mail" value={message} onChange={(e) => setMessage(e.target.value)} />
          </div>
        )}
      </form>
      <div className="mt-3">
        {guests.isPending && <Spinner />}
        {guests.isError && <p className="text-sm text-danger">Could not load guests</p>}
        {guests.data && guests.data.length === 0 && <p className="text-sm text-fg-subtle" data-guests-empty>No guests yet.</p>}
        {guests.data && guests.data.length > 0 && (
          <ul className="divide-y divide-line rounded-md border border-line" aria-label="Guest list">
            {guests.data.map((g) => (
              <li key={g.member.id} className="flex items-center gap-2 px-2 py-1.5 text-sm" data-guest={g.member.id}>
                <Avatar actor={{ display_name: g.member.user.display_name, avatar_url: g.member.user.avatar_url }} size={24} />
                <div className="min-w-0 flex-1">
                  <div className="truncate">
                    {g.member.user.display_name}
                    <Badge tone={g.member.status === 'active' ? 'ok' : g.member.status === 'invited' ? 'info' : 'warn'} className="ml-1">
                      {g.member.status === 'invited' ? 'invited' : g.member.status}
                    </Badge>
                  </div>
                  <div className="flex items-center gap-1 truncate text-xs text-fg-subtle">
                    <Mail size={11} /> {g.member.user.email}
                  </div>
                </div>
                <Select aria-label={`Access for ${g.member.user.email}`} value={g.access} disabled={update.isPending} onChange={(e) => update.mutate({ memberId: g.member.id, access: e.target.value as GuestAccess }, { onError: onErr })} className="h-7 w-auto py-0 text-xs">
                  {ACCESS.map((a) => (
                    <option key={a.value} value={a.value}>
                      {a.label}
                    </option>
                  ))}
                </Select>
                <IconButton label={`Remove ${g.member.user.email}`} size="sm" className="text-danger" disabled={remove.isPending} onClick={() => remove.mutate(g.member.id, { onError: onErr })}>
                  <Trash2 size={13} />
                </IconButton>
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}
